import { useCallback, useEffect, useRef, useState } from "react";
import type { PaginatedResult } from "@akhshy/types";

export interface Paginated<T> {
  items: T[];
  loading: boolean;
  error: Error | null;
  hasMore: boolean;
  total: number;
  loadMore: () => void;
  reset: () => void;
}

/**
 * Server-side pagination hook. Loads page 1 on mount (and whenever `deps`
 * change — e.g. a changed filter), then appends further pages via loadMore
 * while `hasMore`. On a failed page-1 load it falls back to `fallback`
 * capped to a single page with no "load more".
 */
export function usePaginated<T>(
  fetchPage: (page: number) => Promise<PaginatedResult<T>>,
  deps: unknown[] = [],
  fallback: T[] = [],
): Paginated<T> {
  const [items, setItems] = useState<T[]>([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const reqId = useRef(0);

  const load = useCallback(
    (nextPage: number, replace: boolean) => {
      const id = ++reqId.current;
      setLoading(true);
      setError(null);
      fetchPage(nextPage).then(
        (res) => {
          if (id !== reqId.current) return;
          setItems((prev) => (replace ? res.items : [...prev, ...res.items]));
          setPage(res.page);
          setTotal(res.total);
          setHasMore(res.hasMore);
          setLoading(false);
        },
        (err: unknown) => {
          if (id !== reqId.current) return;
          if (replace) {
            setItems(fallback);
            setTotal(fallback.length);
            setHasMore(false);
          }
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    deps,
  );

  useEffect(() => {
    load(1, true);
  }, [load]);

  const loadMore = useCallback(() => {
    if (loading || !hasMore) return;
    load(page + 1, false);
  }, [loading, hasMore, page, load]);

  const reset = useCallback(() => load(1, true), [load]);

  return { items, loading, error, hasMore, total, loadMore, reset };
}
